/**
 * Datas e horários no fuso da profissional.
 *
 * Regras (plano, seção 15.4):
 * - Tudo é gravado em UTC (ISO 8601).
 * - Cada profissional tem um fuso IANA (ex.: America/Manaus).
 * - Disponibilidade e mensagens são calculadas e exibidas no fuso dela.
 *
 * Sem bibliotecas: usamos Intl.DateTimeFormat, que existe no navegador,
 * no Node e no Hermes (React Native).
 */

/** Data local no formato YYYY-MM-DD. */
export type LocalDate = string;
/** Hora local no formato HH:MM (aceita 24:00 como fim do dia). */
export type LocalTime = string;

export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^(\d{2}):(\d{2})$/;

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatterCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
    });
    formatterCache.set(timeZone, f);
  }
  return f;
}

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

export interface ZonedParts {
  date: LocalDate;
  time: LocalTime;
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  /** 0 = domingo ... 6 = sábado */
  weekday: number;
  /** minutos desde a meia-noite local */
  minutes: number;
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    formatter(timeZone);
    return true;
  } catch {
    return false;
  }
}

/** Partes da data/hora local de um instante UTC num fuso. */
export function utcToZoned(utc: number | string | Date, timeZone: string): ZonedParts {
  const ms = toMs(utc);
  const parts = formatter(timeZone).formatToParts(new Date(ms));
  const get = (type: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === type)?.value ?? '';
  const year = Number(get('year'));
  const month = Number(get('month'));
  const day = Number(get('day'));
  let hour = Number(get('hour'));
  if (hour === 24) hour = 0;
  const minute = Number(get('minute'));
  const second = Number(get('second'));
  const weekday = WEEKDAYS[get('weekday')] ?? 0;
  return {
    date: `${pad4(year)}-${pad2(month)}-${pad2(day)}`,
    time: `${pad2(hour)}:${pad2(minute)}`,
    year,
    month,
    day,
    hour,
    minute,
    second,
    weekday,
    minutes: hour * 60 + minute,
  };
}

/** Diferença (em minutos) entre o relógio local do fuso e o UTC naquele instante. */
export function tzOffsetMinutes(utcMs: number, timeZone: string): number {
  const p = utcToZoned(utcMs, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  const truncated = Math.floor(utcMs / 1000) * 1000;
  return Math.round((asUtc - truncated) / MINUTE);
}

/** Converte data + hora locais de um fuso em instante UTC (ms). */
export function zonedToUtc(date: LocalDate, time: LocalTime, timeZone: string): number {
  const { year, month, day } = parseDate(date);
  const minutes = parseTime(time);
  const naive = Date.UTC(year, month - 1, day, 0, minutes);
  const offset1 = tzOffsetMinutes(naive - 0, timeZone);
  let utc = naive - offset1 * MINUTE;
  const offset2 = tzOffsetMinutes(utc, timeZone);
  if (offset2 !== offset1) {
    utc = naive - offset2 * MINUTE;
  }
  return utc;
}

/** Converte para ISO UTC sem milissegundos: 2026-10-10T13:20:00Z */
export function toIso(utc: number | string | Date): string {
  return new Date(toMs(utc)).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

export function toMs(value: number | string | Date): number {
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) throw new Error(`Data inválida: ${value}`);
  return ms;
}

export function parseDate(date: LocalDate): { year: number; month: number; day: number } {
  const m = DATE_RE.exec(date);
  if (!m) throw new Error(`Data inválida: ${date}`);
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

/** Minutos desde a meia-noite. Aceita 24:00 (= 1440). */
export function parseTime(time: LocalTime): number {
  const m = TIME_RE.exec(time);
  if (!m) throw new Error(`Hora inválida: ${time}`);
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 24 || min > 59 || (h === 24 && min !== 0)) throw new Error(`Hora inválida: ${time}`);
  return h * 60 + min;
}

export function formatTime(minutes: number): LocalTime {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${pad2(h)}:${pad2(m)}`;
}

export function isValidDate(date: string): boolean {
  const m = DATE_RE.exec(date);
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() + 1 === Number(m[2]) && d.getUTCDate() === Number(m[3]);
}

export function isValidTime(time: string): boolean {
  try {
    parseTime(time);
    return true;
  } catch {
    return false;
  }
}

export function addDays(date: LocalDate, days: number): LocalDate {
  const { year, month, day } = parseDate(date);
  const d = new Date(Date.UTC(year, month - 1, day + days));
  return `${pad4(d.getUTCFullYear())}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** 0 = domingo ... 6 = sábado */
export function weekdayOf(date: LocalDate): number {
  const { year, month, day } = parseDate(date);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

/** Diferença em dias de calendário (b - a). */
export function diffDays(a: LocalDate, b: LocalDate): number {
  const pa = parseDate(a);
  const pb = parseDate(b);
  return Math.round((Date.UTC(pb.year, pb.month - 1, pb.day) - Date.UTC(pa.year, pa.month - 1, pa.day)) / DAY);
}

/** Lista de datas de `from` até `to`, inclusive. */
export function dateRange(from: LocalDate, to: LocalDate): LocalDate[] {
  const out: LocalDate[] = [];
  const n = diffDays(from, to);
  for (let i = 0; i <= n; i++) out.push(addDays(from, i));
  return out;
}

/** Hoje no fuso. */
export function todayIn(timeZone: string, now: number = Date.now()): LocalDate {
  return utcToZoned(now, timeZone).date;
}

/** Início (UTC ms) e fim exclusivo do dia local. */
export function dayBoundsUtc(date: LocalDate, timeZone: string): { start: number; end: number } {
  return { start: zonedToUtc(date, '00:00', timeZone), end: zonedToUtc(addDays(date, 1), '00:00', timeZone) };
}

/** Segunda-feira da semana da data (semana de segunda a domingo). */
export function startOfWeek(date: LocalDate): LocalDate {
  const wd = weekdayOf(date);
  const delta = wd === 0 ? -6 : 1 - wd;
  return addDays(date, delta);
}

export function startOfMonth(date: LocalDate): LocalDate {
  return `${date.slice(0, 7)}-01`;
}

export function endOfMonth(date: LocalDate): LocalDate {
  const { year, month } = parseDate(date);
  const d = new Date(Date.UTC(year, month, 0));
  return `${pad4(d.getUTCFullYear())}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

export function overlaps(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart < bEnd && bStart < aEnd;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function pad4(n: number): string {
  return String(n).padStart(4, '0');
}

/**
 * O fuso do aparelho, para a conta nascer já no horário certo.
 *
 * Veio de app/demo.ts, que saiu junto com o modo demonstração: nunca foi dado
 * de exemplo, é utilitário de fuso. Cai em São Paulo quando o navegador não
 * sabe responder.
 */
export function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';
  } catch {
    return 'America/Sao_Paulo';
  }
}
